import { makeAutoObservable, reaction, runInAction } from "mobx";
import agent from "../../api/agent";
import { ReportInput } from "../../models/reports";
import { PagingParams, Pagination } from "../../models/pagination";
import { DateRangeOption, ReportType } from "../../models/enums";
import { CoreTrackingIndexDto } from "../../models/coreTracking";
import { CoreTrackingFormValues } from "../../models/inventory";

export default class CoreTrackingIndexStore {
    constructor() {
        makeAutoObservable(this);
        reaction(
            () => this.predicate.keys(),
            () => {
                this.coreTrackingIndexRegistry.clear();
                this.loadCoreTrackingIndex();
            }
        );
    }

    loadingInitial = false;
    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    };

    reportCriteria: ReportInput = new ReportInput({
        dateRange: DateRangeOption.Today,
        startDate: new Date(),
        endDate: new Date(),
        reportType: ReportType.Summary,
        filter: "0",
    });

    coreTrackingIndexRegistry = new Map<string, CoreTrackingIndexDto>();

    resetCoreTrackingIndexRegistry = () => {
        this.coreTrackingIndexRegistry.clear();
    };

    loadCoreTrackingIndex = async () => {
        let resultToReturn: any[] = [];
        this.loadingInitial = true;
        try {
            this.coreTrackingIndexRegistry.clear();
            const result = await agent.coreTracking.getCoreTrackingIndex(this.axiosParams);
            resultToReturn = result.data;
            runInAction(() => {
                result.data.forEach((coreTrackingIdxItem) => {
                    this.setCoreTrackingIndexItem(coreTrackingIdxItem);
                });
                this.setCoreTrackingIndexPagination(result.pagination);
            });
        } catch (error) {
            console.log(error);
        } finally {
            this.setLoadingInitial(false);
            return resultToReturn;
        }
    };
    updateCoreTracking = async (values: { cores: CoreTrackingFormValues[] }) => {
        this.loadingInitial = true;
        try {
            await agent.coreTracking.updateCoreTracking(values);
        } catch (error) {
            console.log('error:', error);
        } finally {
            runInAction(() => {
                this.loadingInitial = false;
            });
        }
    }

    private setCoreTrackingIndexItem = (coreTrackingIdxItem: CoreTrackingIndexDto) => {
        this.coreTrackingIndexRegistry.set(coreTrackingIdxItem.id ?? "", coreTrackingIdxItem);
    };

    get getCoreTrackingIndexItems() {
        return Array.from(this.coreTrackingIndexRegistry.values())
    }

    get groupedCoreTrackingIndexItems() {
        return Object.entries(
            this.getCoreTrackingIndexItems.reduce((groups, invSt) => {
                const name = `${invSt.vendorName
                        ? invSt.vendorName
                        : "No Vendor Assigned"
                    }`;

                groups[name] = groups[name] ? [...groups[name], invSt] : [invSt];
                return groups;
            }, {} as { [key: string]: CoreTrackingIndexDto[] })
        );
    }

    pagingParams = new PagingParams(1, 25);
    pagination: Pagination | null = null;
    predicate = new Map().set("all", true);
    // Paging
    setCoreTrackingIndexPagination = (pagination: Pagination | null) => {
        this.pagination = pagination;
    }
    setCoreTrackingIndexPagingParams = (pagingParams: PagingParams) => {
        this.pagingParams = pagingParams;
    }
    //

    get axiosParams() {
        const params = new URLSearchParams();
        params.append("pageNumber", this.pagingParams.pageNumber.toString());
        params.append("pageSize", this.pagingParams.pageSize.toString());
        this.predicate.forEach((value, key) => {
            if (key === "invoiceDate") {
                params.append(key, (value as Date).toISOString());
            } else {
                params.append(key, value);
            }
        });
        return params;
    }

    setCoreTrackingIndexPredicate = (predicate: string, value?: string) => {
        if (value)
            this.predicate.set(predicate, value);
        else
          this.predicate.delete(predicate);
    }
    resetCoreTrackingIndexPredicate = () => this.predicate.clear();
    
    hasCoreTrackingIndexPredicate = () => {
        return Array.from(this.predicate.keys()).filter(v => v !== 'all').length > 0;
    }

    setReportCriteria = (values: ReportInput) => {
        this.reportCriteria = new ReportInput(values);
    };

    calculateTotalCosts = (data: CoreTrackingIndexDto[]) => {
        const summary = data.reduce(
            (accumulator: number, item: CoreTrackingIndexDto) => {
                accumulator += (item.coreValue ?? 0.0);
                return accumulator;
            }, 0);
        return summary;
    };

}
